import React from 'react';
import { Avatar, Dropdown, Button, Space, Typography } from 'antd';
import { UserOutlined, LogoutOutlined, LoginOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { fct } from '../../fct/tokens/tokens';

const { Text } = Typography;

interface UserProfileProps {
  /** `rail`: dentro del raíl de GcCarcasa (sobre verde oscuro). Por defecto, en una cabecera clara. */
  variante?: 'cabecera' | 'rail';
  /** En el raíl plegado: solo el avatar. */
  compacto?: boolean;
}

/**
 * UserProfile component
 * Displays user avatar, name, and role; provides login/logout functionality.
 */
const UserProfile: React.FC<UserProfileProps> = ({ variante = 'cabecera', compacto = false }) => {
  const { userInfo, isAuthenticated, isLoading, login, logout } = useAuth();
  const { roles } = usePermissions();

  // Menu items for authenticated users
  const menuItems: MenuProps['items'] = [
    {
      key: 'logout',
      label: 'Cerrar sesión',
      icon: <LogoutOutlined />,
      onClick: logout,
    },
  ];

  if (isLoading) {
    return (
      <div style={{ padding: '0 16px' }}>
        <Avatar icon={<UserOutlined />} />
      </div>
    );
  }

  if (!isAuthenticated || !userInfo) {
    return (
      <Button
        type="primary"
        icon={<LoginOutlined />}
        onClick={login}
      >
        Iniciar sesión
      </Button>
    );
  }

  const rol = roles.length > 0 ? roles[0] : 'Sin rol';
  const avatar = userInfo.photoUrl ? (
    <Avatar src={userInfo.photoUrl} size={32} />
  ) : (
    <Avatar icon={<UserOutlined />} size={32} />
  );

  if (variante === 'rail') {
    return (
      <Dropdown menu={{ items: menuItems }} placement="topLeft" arrow>
        <button
          type="button"
          className="fct-rail__item gc-rail__item gc-usuario"
          aria-label={`${userInfo.name} (${rol}). Opciones de sesión`}
          title={compacto ? `${userInfo.name} (${rol})` : undefined}
        >
          {avatar}
          <span className="gc-usuario__texto">
            <span className="gc-usuario__nombre">{userInfo.name}</span>
            <span className="gc-usuario__rol">{rol}</span>
          </span>
        </button>
      </Dropdown>
    );
  }

  return (
    <Dropdown menu={{ items: menuItems }} placement="bottomRight" arrow>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 12px',
          borderRadius: fct.radio.control,
          cursor: 'pointer',
        }}
      >
        {avatar}
        <Space direction="vertical" size={0} style={{ lineHeight: 1.2 }}>
          <Text strong style={{ fontSize: 14, margin: 0 }}>
            {userInfo.name}
          </Text>
          <Text style={{ fontSize: 12, color: fct.tinta['700'] }}>
            ({rol})
          </Text>
        </Space>
      </div>
    </Dropdown>
  );
};

export default UserProfile;
